<template>
  <div>
    <b-row class="mb-2">
      <b-col class="d-flex justify-content-end">
        <b-button variant="success" @click="onAsignarPaquete">
          Asignar paquete
        </b-button>
      </b-col>
    </b-row>

    <b-card class="mb-2">
      <b-form @submit.prevent="onBuscar">
        <b-row>
          <b-col md="4" class="mb-1">
            <b-form-group label="Paciente">
              <b-form-input
                v-model.trim="filtros.paciente"
                placeholder="Buscar por identificación o nombres"
              />
            </b-form-group>
          </b-col>

          <b-col md="4" class="mb-1">
            <b-form-group label="Paquete">
              <b-form-input
                v-model.trim="filtros.paquete"
                placeholder="Nombre del paquete"
              />
            </b-form-group>
          </b-col>

          <b-col md="4" class="mb-1">
            <b-form-group label="Estado">
              <b-form-select
                v-model="filtros.estado"
                :options="estadoOpciones"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="3" class="mb-1">
            <b-form-group label="Fecha desde">
              <b-form-input
                v-model="filtros.fechaDesde"
                type="date"
              />
            </b-form-group>
          </b-col>

          <b-col md="3" class="mb-1">
            <b-form-group label="Fecha hasta">
              <b-form-input
                v-model="filtros.fechaHasta"
                type="date"
              />
            </b-form-group>
          </b-col>

          <b-col md="3" class="mb-1 d-flex align-items-end">
            <b-button type="submit" variant="primary" class="mr-1">
              Buscar
            </b-button>
            <b-button variant="outline-secondary" @click="onLimpiarFiltros">
              Limpiar
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </b-card>

    <b-card>
      <b-overlay :show="loading" rounded="sm" opacity="0.3">
        <b-table
          :items="paquetes"
          :fields="fields"
          responsive
          small
          striped
          empty-text="No se encontraron paquetes asignados"
        >
          <template #cell(paciente)="{ item }">
            <div class="text-nowrap">
              <strong>{{ item.paciente.identificacion }}</strong><br>
              {{ `${item.paciente.primerApellido} ${item.paciente.primerNombre}`.trim() }}
            </div>
          </template>

          <template #cell(paquete)="{ item }">
            <span>{{ item.paquete.nombre }}</span>
          </template>

          <template #cell(fecha)="{ item }">
            {{ item.fechaAsignacion || '-' }}
          </template>

          <template #cell(servicios)="{ item }">
            <div class="servicios-grid">
              <div
                v-for="servicio in serviciosOrdenados"
                :key="servicio.key"
                :class="['servicio-cell', item.servicios[servicio.key] ? 'activo' : 'inactivo', servicio.extraClass]"
              >
                {{ servicio.label }}
              </div>
            </div>
          </template>

          <template #cell(estado)="{ item }">
            <b-badge :variant="estadoVariant(item.estado)">
              {{ mapEstado(item.estado) }}
            </b-badge>
          </template>

          <template #cell(documentos)="{ item }">
            <span v-if="item.documentosFaltantes.length">{{ item.documentosFaltantes.join(', ') }}</span>
            <span v-else>-</span>
          </template>

          <template #cell(acciones)="{ item }">
            <div class="d-flex justify-content-end">
              <b-button
                variant="info"
                size="sm"
                class="mr-1"
                @click="onVer(item)"
                title="Consultar"
              >
                <font-awesome-icon icon="magnifying-glass" />
              </b-button>

              <b-button
                variant="warning"
                size="sm"
                class="mr-1"
                @click="onEditar(item)"
                title="Editar"
              >
                <font-awesome-icon icon="edit" />
              </b-button>

              <b-button
                variant="danger"
                size="sm"
                @click="onAnular(item)"
                title="Anular paquete"
              >
                <font-awesome-icon icon="circle-xmark" />
              </b-button>
            </div>
          </template>
        </b-table>
      </b-overlay>
    </b-card>

    <b-modal
      id="modal-asignar-paquete"
      v-model="showModal"
      size="xl"
      title="Asignación de paquete"
      hide-footer
      no-close-on-backdrop
      no-close-on-esc
      @hidden="onModalHidden"
    >
      <AsignacionPaquetesModal
        ref="modalAsignacion"
        :initial-data="paqueteSeleccionado"
        @saved="onAsignacionGuardada"
      />
    </b-modal>

    <b-modal
      id="modal-resumen-paquete-asignado"
      v-model="resumenModalVisible"
      title="Resumen del paquete asignado"
      size="xl"
      hide-footer
      @hidden="onResumenModalHidden"
    >
      <b-overlay :show="resumenLoading" rounded="sm" opacity="0.15">
        <div v-if="resumenDetalle">
          <ResumenAsignacionPaquete
            :resumen-paciente="resumenPaciente"
            :resumen-paquete="resumenPaquete"
            :resumen-servicios="resumenServicios"
            :resumen-documentos="resumenDocumentosLista"
            @ver-documento="onVerDocumentoResumen"
          />
        </div>
        <div v-else-if="!resumenLoading" class="text-center py-3">
          <p class="mb-0 text-muted">No se encontró información para mostrar.</p>
        </div>
        <template #overlay>
          <div class="d-flex justify-content-center align-items-center w-100 h-100">
            <b-spinner small class="mr-2" /> Cargando resumen...
          </div>
        </template>
      </b-overlay>
    </b-modal>

    <ModalViewFile
      v-if="visorDocumento.url"
      v-model="visorDocumento.visible"
      :url-file="visorDocumento.url"
      :ruta-completa="visorDocumento.rutaCompleta"
      @hidden="resetVisorDocumento"
    />
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BButton,
  BCard,
  BOverlay,
  BTable,
  BBadge,
  BModal,
  BSpinner,
  BForm,
  BFormGroup,
  BFormInput,
  BFormSelect,
} from 'bootstrap-vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import AsignacionPaquetesModal from '@/views/enfermedadesPreventivas/components/AsignacionPaquetesModal.vue';
import ResumenAsignacionPaquete from '@/views/enfermedadesPreventivas/components/ResumenAsignacionPaquete.vue';
import { ModalViewFile } from '@/shared/components';
import { paquetesAsignadosService } from '@/views/enfermedadesPreventivas/services/paquetesAsignadosService';

const SERVICIOS_ORDEN = [
  { key: 'atenciones', label: 'Atenciones' },
  { key: 'imagenes', label: 'Imágenes' },
  { key: 'laboratorios', label: 'Laboratorios' },
  { key: 'terapias', label: 'Terapias' },
  { key: 'procedimientos', label: 'Procedimientos', extraClass: 'span-2' },
];

export default {
  name: 'ConsultarPaqueteAsignado',
  components: {
    BRow,
    BCol,
    BButton,
    BCard,
    BOverlay,
    BTable,
    BBadge,
    BModal,
    BSpinner,
    BForm,
    BFormGroup,
    BFormInput,
    BFormSelect,
    AsignacionPaquetesModal,
    ResumenAsignacionPaquete,
    ModalViewFile,
    FontAwesomeIcon,
  },
  data() {
    return {
      loading: false,
      paquetes: [],
      showModal: false,
      paqueteSeleccionado: null,
      resumenModalVisible: false,
      resumenLoading: false,
      resumenDetalle: null,
      resumenDocumentos: {
        cedula: null,
        otro: null,
      },
      visorDocumento: {
        visible: false,
        url: null,
        rutaCompleta: false,
      },
      filtros: {
        paciente: '',
        paquete: '',
        estado: '1',
        fechaDesde: '',
        fechaHasta: '',
      },
      estadoOpciones: [
        { value: '1', text: 'Asignado' },
        { value: '2', text: 'En uso' },
        { value: '3', text: 'Terminado' },
      ],
      fields: [
        { key: 'paciente', label: 'Paciente', thClass: 'text-center' },
        { key: 'paquete', label: 'Paquete', thClass: 'text-center' },
        { key: 'fecha', label: 'Fecha', thClass: 'text-center' },
        { key: 'servicios', label: 'Servicios', thClass: 'text-center' },
        { key: 'estado', label: 'Estado del paquete', thClass: 'text-center' },
        { key: 'documentos', label: 'Documento faltante', thClass: 'text-center' },
        { key: 'acciones', label: 'Acciones', thClass: 'text-center', tdClass: 'text-right' },
      ],
      serviciosOrdenados: SERVICIOS_ORDEN,
    };
  },
  computed: {
    resumenPaciente() {
      if (!this.resumenDetalle?.paciente) return null;
      const paciente = this.resumenDetalle.paciente;
      const persona = paciente.persona || {};
      const identificacion = paciente.identificacion || persona.identificacion || '-';
      const nombres = this.obtenerNombrePaciente(paciente);
      const edad = persona.fechaNacimiento ? this.calcularEdad(persona.fechaNacimiento) : '-';

      return {
        identificacion: identificacion || '-',
        nombres,
        edad,
      };
    },
    resumenPaquete() {
      if (!this.resumenDetalle?.paquete) return null;
      const { paquete } = this.resumenDetalle;
      const precio = paquete.precio != null
        ? paquete.precio
        : (paquete.jsonParsed?.precio ?? paquete.jsonParsed?.precios?.valor ?? null);

      return {
        nombre: paquete.nombre || 'Paquete sin nombre',
        precio: precio != null ? this.formatCurrency(precio) : null,
      };
    },
    resumenServicios() {
      const resumenList = this.resumenDetalle?.serviciosResumen;
      if (Array.isArray(resumenList) && resumenList.length) return resumenList;

      if (!this.resumenDetalle?.serviciosDetalle) return [];
      const mapTitles = {
        imagenes: 'Imágenes',
        laboratorios: 'Laboratorios',
        procedimientos: 'Procedimientos',
        terapias: 'Terapias',
        atenciones: 'Atenciones médicas',
      };

      return Object.keys(mapTitles)
        .map(key => {
          const lista = Array.isArray(this.resumenDetalle.serviciosDetalle[key])
            ? this.resumenDetalle.serviciosDetalle[key]
            : [];
          if (!lista.length) return null;

          const items = lista.map(item => (
            key === 'atenciones'
              ? this.formatearResumenAtencion(item)
              : this.obtenerDescripcionServicio(item)
          ));

          return {
            key,
            title: mapTitles[key],
            items,
          };
        })
        .filter(Boolean);
    },
    resumenDocumentosLista() {
      const entries = [
        { key: 'cedula', label: 'Cédula de identidad' },
        { key: 'otro', label: 'Otros documentos' },
      ];

      return entries.map(item => ({
        ...item,
        url: this.obtenerUrlDocumento(item.key),
        disponible: this.tieneDocumentoDisponible(item.key),
      }));
    },
  },
  created() {
    this.fetchListado(this.filtros);
  },
  methods: {
    async fetchListado(filtros = {}) {
      this.loading = true;
      const params = {
        paciente: filtros.paciente ? filtros.paciente.trim() : null,
        paquete: filtros.paquete ? filtros.paquete.trim() : null,
        estado: filtros.estado || null,
        fechaDesde: filtros.fechaDesde || null,
        fechaHasta: filtros.fechaHasta || null,
      };

      try {
        const { data } = await paquetesAsignadosService.getListado(params);
        this.paquetes = data;
      } catch (error) {
        console.error('Error al cargar paquetes asignados', error);
        this.$bvToast?.toast('No se pudo cargar el listado de paquetes asignados.', {
          variant: 'danger',
          solid: true,
        });
      } finally {
        this.loading = false;
      }
    },
    onBuscar() {
      const { fechaDesde, fechaHasta } = this.filtros;
      if (fechaDesde && fechaHasta) {
        const desde = new Date(fechaDesde);
        const hasta = new Date(fechaHasta);
        if (Number.isNaN(desde.getTime()) || Number.isNaN(hasta.getTime())) {
          this.$bvToast?.toast('Verifica las fechas ingresadas.', { variant: 'warning', solid: true });
          return;
        }
        if (desde > hasta) {
          this.$bvToast?.toast('La fecha inicial no puede ser mayor que la fecha final.', { variant: 'warning', solid: true });
          return;
        }
      }

      this.fetchListado({ ...this.filtros });
    },
    onLimpiarFiltros() {
      this.filtros = {
        paciente: '',
        paquete: '',
        estado: '1',
        fechaDesde: '',
        fechaHasta: '',
      };
      this.fetchListado(this.filtros);
    },
    clonar(obj) {
      try {
        return obj == null ? obj : JSON.parse(JSON.stringify(obj));
      } catch (error) {
        console.error('No se pudo clonar el objeto', error);
        return obj;
      }
    },
    construirDetalleDesdeItem(item) {
      if (!item) return null;
      const serviciosDetalle = this.clonar(item.serviciosDetalle || {});
      const tieneDetalle = serviciosDetalle
        ? Object.values(serviciosDetalle).some(lista => Array.isArray(lista) && lista.length)
        : false;
      const serviciosResumen = tieneDetalle
        ? []
        : (Array.isArray(item.serviciosResumen) ? this.clonar(item.serviciosResumen) : []);
      const documentos = this.construirDocumentosDesdeItem(item);

      return {
        id: item.id,
        token: item.token,
        fechaAsignacion: item.fechaAsignacion,
        estado: item.estado,
        encuesta: item.encuesta ?? null,
        paciente: this.clonar(item.paciente),
        paquete: this.clonar(item.paquete),
        serviciosResumen,
        serviciosDetalle,
        servicios: this.clonar(item.servicios),
        documentos,
      };
    },
    construirDocumentosDesdeItem(item) {
      const faltantes = Array.isArray(item?.documentosFaltantes)
        ? item.documentosFaltantes.map(valor => valor?.toString()?.toLowerCase()?.trim()).filter(Boolean)
        : [];

      const esFaltante = palabra => faltantes.some(texto => texto.includes(palabra));

      const baseDocumentos = item?.documentos && typeof item.documentos === 'object'
        ? { ...item.documentos }
        : { cedula: null, otro: null };

      return {
        cedula: esFaltante('cedula') || esFaltante('cédula') ? null : baseDocumentos.cedula || null,
        otro: esFaltante('otro') ? null : baseDocumentos.otro || null,
      };
    },
    async obtenerDetallePaquete(item, { mostrarMensaje = true } = {}) {
      if (!item) return null;
      const token = item?.token || item?.paquete?.token || null;
      if (!token) {
        if (mostrarMensaje) {
          this.$bvToast?.toast('No se encontró el identificador del paquete asignado.', { variant: 'danger', solid: true });
        }
        return this.construirDetalleDesdeItem(item);
      }

      try {
        const detalle = await paquetesAsignadosService.getDetalle({ token });
        if (detalle) return detalle;
      } catch (error) {
        console.error('Error consultando el detalle del paquete asignado', error);
        if (mostrarMensaje) {
          const message = error?.message || 'No se pudo obtener el detalle del paquete asignado.';
          this.$bvToast?.toast(`${message} Se mostrará la información disponible.`, {
            variant: 'warning',
            solid: true,
          });
        }
      }

      return this.construirDetalleDesdeItem(item);
    },
    onAsignarPaquete() {
      this.paqueteSeleccionado = null;
      this.showModal = true;
    },
    async onVer(item) {
      this.resumenDetalle = null;
      this.resumenDocumentos = { cedula: null, otro: null };
      this.resumenModalVisible = true;
      this.resumenLoading = true;

      try {
        const detalle = await this.obtenerDetallePaquete(item, { mostrarMensaje: true });
        this.resumenDetalle = detalle;
        this.resumenDocumentos = detalle && detalle.documentos ? { ...detalle.documentos } : { cedula: null, otro: null };
      } finally {
        this.resumenLoading = false;
      }
    },
    async onEditar(item) {
      this.loading = true;
      this.paqueteSeleccionado = null;

      try {
        const detalle = await this.obtenerDetallePaquete(item, { mostrarMensaje: true });
        if (!detalle) throw new Error('No se pudo obtener la información del paquete asignado.');

        this.paqueteSeleccionado = { ...detalle, modoEdicion: true };
        this.showModal = true;
      } catch (error) {
        console.error('Error al cargar el paquete asignado para edición', error);
        const message = error?.message || 'No se pudo cargar la información del paquete asignado.';
        this.$bvToast?.toast(message, { variant: 'danger', solid: true });
      } finally {
        this.loading = false;
      }
    },
    onAnular(item) {
      this.$swal?.fire({
        title: 'Anular paquete',
        text: `¿Desea anular el paquete asignado a ${item.paciente.primerApellido} ${item.paciente.primerNombre}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, anular',
        cancelButtonText: 'Cancelar',
      });
    },
    onModalHidden() {
      this.paqueteSeleccionado = null;
      this.$refs.modalAsignacion?.resetFormulario?.();
    },
    onAsignacionGuardada() {
      this.showModal = false;
      this.fetchListado(this.filtros);
    },
    onResumenModalHidden() {
      this.resumenDetalle = null;
      this.resumenDocumentos = { cedula: null, otro: null };
      this.resetVisorDocumento();
    },
    estadoVariant(estado) {
      const map = {
        '1': 'primary',
        '2': 'warning',
        '3': 'success',
        asignado: 'primary',
        'en uso': 'warning',
        uso: 'warning',
        terminado: 'success',
      };
      const key = estado ? estado.toString().toLowerCase() : '';
      return map[key] || 'secondary';
    },
    mapEstado(estado) {
      if (!estado) return 'Asignado';
      const text = estado.toString().toLowerCase();
      switch (text) {
        case '1':
        case 'asignado':
          return 'Asignado';
        case '2':
        case 'en uso':
        case 'uso':
          return 'En uso';
        case '3':
        case 'terminado':
          return 'Terminado';
        default:
          return estado;
      }
    },
    obtenerNombrePaciente(paciente) {
      if (!paciente) return '-';
      const persona = paciente.persona || {};
      const partes = [
        persona.primerNombre || paciente.primerNombre,
        persona.segundoNombre || paciente.segundoNombre,
        persona.primerApellido || paciente.primerApellido,
        persona.segundoApellido || paciente.segundoApellido,
      ].filter(Boolean);

      if (partes.length) return partes.join(' ');
      return paciente.nombreCompleto || paciente.fullName || paciente.label || '-';
    },
    calcularEdad(fechaNacimiento) {
      if (!fechaNacimiento) return '-';
      const fecha = new Date(fechaNacimiento);
      if (Number.isNaN(fecha.getTime())) return '-';

      const hoy = new Date();
      let edad = hoy.getFullYear() - fecha.getFullYear();
      const mes = hoy.getMonth() - fecha.getMonth();
      if (mes < 0 || (mes === 0 && hoy.getDate() < fecha.getDate())) edad -= 1;

      return edad >= 0 ? edad : '-';
    },
    formatCurrency(value) {
      try {
        return new Intl.NumberFormat('es-EC', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
        }).format(Number(value));
      } catch (error) {
        return value;
      }
    },
    obtenerDescripcionServicio(item = {}) {
      return item.descripcion || item.detalle || item.nombre || item.label || 'Sin descripción';
    },
    obtenerNombreDoctor(item = {}) {
      const doctor = item.doctor || item.doctorNombre || item.medico || item.professional;
      if (!doctor) return '';
      if (typeof doctor === 'string') return doctor;
      if (doctor.text) return doctor.text;
      const persona = doctor.idPersona || doctor.persona;
      if (persona && (persona.primerNombre || persona.primerApellido)) {
        return `${persona.primerApellido || ''} ${persona.primerNombre || ''}`.trim();
      }
      if (doctor.primerNombre || doctor.primerApellido) {
        return `${doctor.primerApellido || ''} ${doctor.primerNombre || ''}`.trim();
      }
      return '';
    },
    formatearFechaCita(valor) {
      if (!valor) return '-';
      const str = String(valor).trim();
      if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
        const [anio, mes, dia] = str.split('-');
        return `${dia}/${mes}/${anio}`;
      }
      return str;
    },
    formatearHoraCita(valor) {
      if (!valor) return '-';
      const str = String(valor).trim();
      const match = str.match(/^(\d{2}):(\d{2})(?::\d{2})?$/);
      if (match) return `${match[1]}:${match[2]}`;
      return str;
    },
    formatearResumenAtencion(item = {}) {
      const especialidadRaw = item.especialidad_text || item.especialidadDescripcion || item.descripcion || 'Sin especialidad';
      const especialidad = typeof especialidadRaw === 'string' ? especialidadRaw.toUpperCase() : especialidadRaw;
      const doctor = this.obtenerNombreDoctor(item) || 'Sin doctor asignado';
      const modalidad = item.modalidadDescripcion || item.tipoAtencion || item.modalidad || 'Sin modalidad';
      const fecha = item.fechaCita ? this.formatearFechaCita(item.fechaCita) : 'Sin fecha';
      const hora = item.horaCita ? this.formatearHoraCita(item.horaCita) : 'Sin hora';

      return `Especialidad: ${especialidad} | Doctor asignado: ${doctor} | Modalidad: ${modalidad} | Fecha: ${fecha} | Hora: ${hora}`;
    },
    obtenerUrlDocumento(key) {
      const documentos = this.resumenDocumentos || {};
      const doc = documentos[key];
      if (!doc) return null;
      if (typeof doc === 'string') return doc;
      if (doc && typeof doc === 'object') return doc.url || doc.path || null;
      return null;
    },
    tieneDocumentoDisponible(key) {
      const url = this.obtenerUrlDocumento(key);
      if (url) return true;

      const doc = (this.resumenDocumentos || {})[key];
      if (!doc) return false;
      if (typeof File !== 'undefined' && doc instanceof File) return true;
      return false;
    },
    onVerDocumentoResumen(documento) {
      if (!documento || !documento.key) return;
      this.abrirDocumentoResumen(documento.key);
    },
    async abrirDocumentoResumen(key) {
      try {
        const url = this.obtenerUrlDocumento(key);
        if (url) {
          this.visorDocumento.url = url;
          this.visorDocumento.rutaCompleta = this.debeUsarRutaCompleta(url);
          this.visorDocumento.visible = true;
          return;
        }

        const doc = (this.resumenDocumentos || {})[key];
        if (typeof File !== 'undefined' && doc instanceof File) {
          const dataUrl = await this.leerArchivoComoDataUrl(doc);
          if (dataUrl) {
            this.visorDocumento.url = dataUrl;
            this.visorDocumento.rutaCompleta = true;
            this.visorDocumento.visible = true;
          }
        }
      } catch (error) {
        console.error('Error al abrir documento del resumen de paquete asignado', error);
        this.$bvToast?.toast('No se pudo abrir el documento seleccionado.', { variant: 'danger', solid: true });
      }
    },
    leerArchivoComoDataUrl(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = event => resolve(event.target?.result || null);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },
    debeUsarRutaCompleta(url) {
      if (!url || typeof url !== 'string') return false;
      return /^(data:|https?:|blob:)/i.test(url.trim());
    },
    resetVisorDocumento() {
      this.visorDocumento.visible = false;
      this.visorDocumento.url = null;
      this.visorDocumento.rutaCompleta = false;
    },
  },
};
</script>

<style scoped>
.servicios-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.3rem;
}

.servicio-cell {
  text-align: center;
  padding: 0.3rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  text-transform: capitalize;
  background-color: #dfe3e6;
  color: #4a4a4a;
}

.servicio-cell.activo {
  background-color: #0ab39c;
  color: white;
}

.servicio-cell.inactivo {
  background-color: #f1f1f1;
  color: #6c757d;
}

.servicio-cell.span-2 {
  grid-column: span 2;
}

.encuesta-input {
  max-width: 120px;
  font-size: 1.5rem;
  text-align: center;
}
</style>
